'use client';

import { c as _c } from "react/compiler-runtime";
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import { useMediaQuery } from 'usehooks-ts';
import { ArrowRight, Shield, Lock, ArrowUpRight } from 'lucide-react';
import Image from 'next/image';
import type { Bank } from '@/utils/bankImageMapper';
import { Spinner } from '@/components/ui/spinner';
import { useEffect } from 'react';
import { toast } from '@/components/toast';
interface BankConnectionConfirmModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  selectedBank: Bank | null;
  isSaving?: boolean;
  loadingStep?: 'idle' | 'saving' | 'connecting' | 'redirecting' | 'verifying_auth';
  closeable?: boolean;
}
const ModalContent = t0 => {
  const $ = _c(25);
  const {
    selectedBank,
    onConfirm,
    isSaving,
    loadingStep: t1
  } = t0;
  const loadingStep = t1 === undefined ? "idle" : t1;
  if (!selectedBank) {
    return null;
  }
  let t2;
  if ($[0] !== loadingStep) {
    t2 = () => {
      switch (loadingStep) {
        case "saving":
          {
            return "Salvando informa\xE7\xF5es...";
          }
        case "connecting":
          {
            return "Conectando com o banco...";
          }
        case "redirecting":
          {
            return "Redirecionando...";
          }
        case "verifying_auth":
          {
            return "Verificando autentica\xE7\xE3o...";
          }
        default:
          {
            return "Confirmar no banco";
          }
      }
    };
    $[0] = loadingStep;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const getLoadingMessage = t2;
  let t3;
  if ($[2] !== selectedBank.name) {
    t3 = <div className="relative"><div className="relative size-12 border-2 border-border rounded-xl overflow-hidden flex justify-center items-center bg-background"><Image src="/images/pierre_white_new_logo.png" alt={selectedBank.name} width={35} height={35} quality={100} className="object-cover hidden dark:block" /><Image src="/images/pierre_dark.png" alt={selectedBank.name} width={48} height={48} quality={100} className="object-cover dark:hidden" /></div></div>;
    $[2] = selectedBank.name;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <ArrowRight className="size-6 text-primary/40" />;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const t5 = selectedBank.image || "/images/banks/banco-do-brasil.svg";
  let t6;
  if ($[5] !== selectedBank.name || $[6] !== t5) {
    t6 = <div className="relative size-12 border-2 border-border rounded-xl overflow-hidden flex justify-center items-center bg-background"><Image src={t5} alt={selectedBank.name} width={48} height={48} quality={100} className="object-cover" /></div>;
    $[5] = selectedBank.name;
    $[6] = t5;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] !== t3 || $[9] !== t6) {
    t7 = <div className="flex items-center justify-between gap-4 pt-2"><div className="flex items-center gap-3">{t3}{t4}{t6}</div></div>;
    $[8] = t3;
    $[9] = t6;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <div className="flex flex-col gap-2"><h2 className="text-primary text-xl font-semibold">Confirme para conectar</h2></div>;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  let t9;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <div className="flex items-start gap-3"><div className="size-5 flex items-center justify-center mt-0.5"><Shield className="size-5 text-primary/60" /></div><p className="text-primary/80 text-sm">Esta é uma etapa mediada pelo Open Finance</p></div>;
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  let t10;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <div className="flex items-start gap-3"><div className="size-5 flex items-center justify-center mt-0.5"><Lock className="size-5 text-primary/60" /></div><p className="text-primary/80 text-sm">Os seus dados e conta estão protegidos</p></div>;
    $[13] = t10;
  } else {
    t10 = $[13];
  }
  let t11;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <div className="flex flex-col gap-4">{t9}{t10}<div className="flex items-start gap-3"><div className="size-5 flex items-center justify-center mt-0.5"><ArrowUpRight className="size-5 text-primary/60" /></div><p className="text-primary/80 text-sm">Você será redirecionado para outra plataforma</p></div></div>;
    $[14] = t11;
  } else {
    t11 = $[14];
  }
  let t12;
  if ($[15] !== getLoadingMessage || $[16] !== isSaving) {
    t12 = isSaving ? <div className="flex items-center gap-2"><Spinner size="lg" spinnerClassName="bg-white dark:bg-black" /><span>{getLoadingMessage()}</span></div> : getLoadingMessage();
    $[15] = getLoadingMessage;
    $[16] = isSaving;
    $[17] = t12;
  } else {
    t12 = $[17];
  }
  let t13;
  if ($[18] !== isSaving || $[19] !== onConfirm || $[20] !== t12) {
    t13 = <div className="pt-2"><Button type="button" rounded="full" className="font-semibold w-full" onClick={onConfirm} size="xl" disabled={isSaving}>{t12}</Button></div>;
    $[18] = isSaving;
    $[19] = onConfirm;
    $[20] = t12;
    $[21] = t13;
  } else {
    t13 = $[21];
  }
  let t14;
  if ($[22] !== t13 || $[23] !== t7) {
    t14 = <div className="flex flex-col gap-6">{t7}{t8}{t11}{t13}</div>;
    $[22] = t13;
    $[23] = t7;
    $[24] = t14;
  } else {
    t14 = $[24];
  }
  return t14;
};
export const BankConnectionConfirmModal = t0 => {
  const $ = _c(32);
  const {
    isOpen,
    onClose,
    onConfirm,
    selectedBank,
    isSaving,
    loadingStep: t1,
    closeable: t2
  } = t0;
  const loadingStep = t1 === undefined ? "idle" : t1;
  const closeable = t2 === undefined ? true : t2;
  const isDesktop = useMediaQuery("(min-width: 768px)");
  let t3;
  if ($[0] !== closeable || $[1] !== onClose) {
    t3 = open => {
      if (!open && !closeable) {
        return;
      }
      onClose();
    };
    $[0] = closeable;
    $[1] = onClose;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const handleOpenChange = t3;
  let t4;
  if ($[3] !== selectedBank?.health?.stage || $[4] !== selectedBank?.health?.status) {
    const getAlertConfig = () => {
      const isBeta = selectedBank?.health?.stage === "BETA";
      const status = selectedBank?.health?.status?.toLowerCase();
      const isOffline = status === "offline";
      const isUnstable = status === "unstable" || status === "inst\xE1vel";
      if (isOffline && isBeta) {
        return {
          show: true,
          type: "error" as const,
          title: "Institui\xE7\xE3o offline e em fase beta",
          message: "A institui\xE7\xE3o selecionada est\xE1 indispon\xEDvel no momento e ainda est\xE1 em fase de testes. Recomendamos tentar novamente mais tarde ou escolher outra institui\xE7\xE3o."
        };
      }
      if (isOffline) {
        return {
          show: true,
          type: "error" as const,
          title: "Institui\xE7\xE3o offline",
          message: "A institui\xE7\xE3o selecionada est\xE1 indispon\xEDvel no momento. Recomendamos tentar novamente mais tarde ou escolher outra institui\xE7\xE3o."
        };
      }
      if (isUnstable && isBeta) {
        return {
          show: true,
          type: "warning" as const,
          title: "Institui\xE7\xE3o em fase beta com instabilidades",
          message: "A institui\xE7\xE3o selecionada ainda est\xE1 em fase de testes e apresentando instabilidades. Caso encontre problemas durante a conex\xE3o, tente novamente mais tarde."
        };
      }
      if (isUnstable) {
        return {
          show: true,
          type: "warning" as const,
          title: "Institui\xE7\xE3o apresentando instabilidades",
          message: "A institui\xE7\xE3o selecionada est\xE1 apresentando instabilidades no momento. Caso ocorra algum problema durante a conex\xE3o, tente novamente mais tarde."
        };
      }
      if (isBeta) {
        return {
          show: true,
          type: "warning" as const,
          title: "Institui\xE7\xE3o em fase beta",
          message: "A institui\xE7\xE3o selecionada ainda est\xE1 em fase de testes e pode apresentar eventuais problemas durante a conex\xE3o."
        };
      }
      return {
        show: false
      };
    };
    t4 = getAlertConfig();
    $[3] = selectedBank?.health?.stage;
    $[4] = selectedBank?.health?.status;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const alertConfig = t4;
  let t5;
  let t6;
  if ($[6] !== alertConfig.message || $[7] !== alertConfig.show || $[8] !== alertConfig.title || $[9] !== alertConfig.type || $[10] !== isOpen) {
    t5 = () => {
      let toastId;
      if (isOpen && alertConfig.show && alertConfig.title) {
        toastId = toast({
          type: alertConfig.type === "error" ? "error" : "info",
          title: alertConfig.title,
          description: alertConfig.message,
          duration: Number.POSITIVE_INFINITY,
          containerClassName: "toast-mobile:!w-max max-w-[650px]"
        });
      }
      return () => {
        if (toastId) {
          toast.dismiss(toastId);
        }
      };
    };
    t6 = [isOpen, alertConfig.show, alertConfig.title, alertConfig.message, alertConfig.type];
    $[6] = alertConfig.message;
    $[7] = alertConfig.show;
    $[8] = alertConfig.title;
    $[9] = alertConfig.type;
    $[10] = isOpen;
    $[11] = t5;
    $[12] = t6;
  } else {
    t5 = $[11];
    t6 = $[12];
  }
  useEffect(t5, t6);
  if (isDesktop) {
    let t7;
    if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = <DialogTitle className="hidden" />;
      $[13] = t7;
    } else {
      t7 = $[13];
    }
    let t8;
    if ($[14] !== isSaving || $[15] !== loadingStep || $[16] !== onConfirm || $[17] !== selectedBank) {
      t8 = <DialogContent className="sm:max-w-[440px] p-6">{t7}<ModalContent selectedBank={selectedBank} onConfirm={onConfirm} isSaving={isSaving} loadingStep={loadingStep} /></DialogContent>;
      $[14] = isSaving;
      $[15] = loadingStep;
      $[16] = onConfirm;
      $[17] = selectedBank;
      $[18] = t8;
    } else {
      t8 = $[18];
    }
    let t9;
    if ($[19] !== handleOpenChange || $[20] !== isOpen || $[21] !== t8) {
      t9 = <Dialog open={isOpen} onOpenChange={handleOpenChange}>{t8}</Dialog>;
      $[19] = handleOpenChange;
      $[20] = isOpen;
      $[21] = t8;
      $[22] = t9;
    } else {
      t9 = $[22];
    }
    return t9;
  }
  let t7;
  if ($[23] !== isSaving || $[24] !== loadingStep || $[25] !== onConfirm || $[26] !== selectedBank) {
    t7 = <SheetContent side="bottom" className="rounded-t-3xl p-6" title="Confirme para conectar" description={"Confirme a conex\xE3o com o banco selecionado"}><ModalContent selectedBank={selectedBank} onConfirm={onConfirm} isSaving={isSaving} loadingStep={loadingStep} /></SheetContent>;
    $[23] = isSaving;
    $[24] = loadingStep;
    $[25] = onConfirm;
    $[26] = selectedBank;
    $[27] = t7;
  } else {
    t7 = $[27];
  }
  let t8;
  if ($[28] !== handleOpenChange || $[29] !== isOpen || $[30] !== t7) {
    t8 = <Sheet open={isOpen} onOpenChange={handleOpenChange}>{t7}</Sheet>;
    $[28] = handleOpenChange;
    $[29] = isOpen;
    $[30] = t7;
    $[31] = t8;
  } else {
    t8 = $[31];
  }
  return t8;
};